import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Brain, Network, Wrench, GraduationCap, ArrowRight, CheckCircle, Clock, Shield, Target, Lightbulb } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { Helmet } from 'react-helmet-async';
import { Card } from './ui/Button';
import { CONTACT_DETAILS } from '@/config/contactDetails';

export const ServicesPage: React.FC = () => {
    const location = useLocation();
    const { trackPageView, trackClick, isSessionReady } = useEventTracking();

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/services');
        }
    }, [isSessionReady]);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }, 100);
            }
        }
    }, [location.hash]);

    const handleCTAClick = (ctaType: string) => {
        trackClick(`services-cta-${ctaType}`, `Services ${ctaType} CTA`, 'button');
    };

    const handleServiceClick = (serviceName: string) => {
        trackClick(`service-${serviceName}`, `Service - ${serviceName}`, 'section');
    };

    const services = [
        {
            id: 'predictive-intelligence',
            icon: <Brain className="h-12 w-12 text-blue-600" />,
            title: 'Predictive Intelligence Platform',
            subtitle: 'AI-powered insights across your operations',
            description: `${CONTACT_DETAILS.company.name} connects to your existing systems and uses AI to identify patterns that indicate future issues or opportunities. Whether you're monitoring equipment in manufacturing, patient outcomes in healthcare, or yield predictions in agriculture - you get actionable insights with time to act strategically instead of reactively.`,
            benefits: [
                'Early warning alerts before issues materialize',
                'Pattern recognition across operational data',
                'Context-specific recommendations, not just alerts',
                'Works across diverse industry systems'
            ],
            ideal: 'Organizations with critical operations that cannot afford unexpected disruptions or missed opportunities'
        },
        {
            id: 'system-integration',
            icon: <Network className="h-12 w-12 text-green-600" />,
            title: 'System Integration',
            subtitle: 'Connect existing data sources seamlessly',
            description: `${CONTACT_DETAILS.company.name} starts by integrating with your existing systems through software connections - PLC/SCADA in industrial settings, EHR in healthcare, IoT sensors in agriculture. As data flows and value is proven, additional monitoring capabilities can be added when justified by ROI.`,
            benefits: [
                'Start with existing infrastructure',
                'Prove value before additional investment',
                'Scalable approach - software integration first',
                'Compatible with industry-standard systems'
            ],
            ideal: 'Operations with existing data systems looking to add predictive intelligence without major disruption'
        },
        {
            id: 'implementation-support',
            icon: <Wrench className="h-12 w-12 text-purple-600" />,
            title: 'Implementation Support',
            subtitle: 'Full technical setup and integration',
            description: `${CONTACT_DETAILS.company.name} handles the complete technical implementation - connecting systems, configuring the platform, integrating with your workflows. Your team focuses on core business operations while all technical complexity is managed for you.`,
            benefits: [
                'Minimal operational disruption during setup',
                'Full integration with existing workflows',
                'All technical complexity handled',
                'Cross-industry implementation expertise'
            ],
            ideal: 'Organizations without dedicated technical teams for advanced analytics implementations'
        },
        {
            id: 'training-enablement',
            icon: <GraduationCap className="h-12 w-12 text-orange-600" />,
            title: 'Training & Enablement',
            subtitle: 'Team education and adoption support',
            description: `${CONTACT_DETAILS.company.name} ensures your team understands how to interpret insights, respond to recommendations, and use the system effectively. Comprehensive training means no one gets left behind as your organization moves from reactive to predictive operations.`,
            benefits: [
                'Your team gains valuable predictive skills',
                'Reduces dependence on external consultants',
                'Complete documentation and procedures',
                'Ongoing support during transition'
            ],
            ideal: 'Teams transitioning from reactive decision-making to predictive intelligence approaches'
        }
    ];

    return (
        <>
            <Helmet>
                <title>{`Predictive Intelligence Services | Cross-Industry AI Solutions | ${CONTACT_DETAILS.company.name}`}</title>
                <meta name="description" content="Comprehensive predictive intelligence services including AI platform implementation, system integration, technical support, and team training across healthcare, manufacturing, and energy." />
                <meta name="keywords" content="predictive intelligence services, AI implementation, system integration, cross-industry analytics, predictive platform" />
                <link rel="canonical" href="https://4castlabs.com/services" />
                <meta property="og:title" content={`Predictive Intelligence Services | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content="Comprehensive predictive intelligence services - AI platform implementation, system integration, and team training." />
                <meta property="og:url" content="https://4castlabs.com/services" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://4castlabs.com/"
                        }, {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "Services",
                            "item": "https://4castlabs.com/services"
                        }]
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Hero Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            Predictive Intelligence Services
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                            From system integration to team training, {CONTACT_DETAILS.company.name} provides
                            comprehensive support
                            to transform your decision-making approach from reactive to predictive - across any
                            industry.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4 mb-12">
                            <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm">
                                <Clock className="h-5 w-5 text-blue-600 mr-2"/>
                                <span className="text-sm font-medium">Early Warning Intelligence</span>
                            </div>
                            <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm">
                                <Shield className="h-5 w-5 text-green-600 mr-2"/>
                                <span className="text-sm font-medium">Minimal Disruption Setup</span>
                            </div>
                            <div className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm">
                                <Target className="h-5 w-5 text-purple-600 mr-2"/>
                                <span className="text-sm font-medium">ROI-Focused Approach</span>
                            </div>
                        </div>

                        {/* Pilot Partner Callout */}
                        <div
                            className="bg-gradient-to-r from-primary-50 to-blue-50 border-l-4 border-primary-500 p-6 rounded-r-lg max-w-4xl mx-auto mb-8">
                            <div className="flex items-center">
                                <Lightbulb className="h-6 w-6 text-primary-600 mr-3 flex-shrink-0"/>
                                <div className="text-left">
                                    <p className="text-lg font-semibold text-primary-900 mb-1">
                                        Seeking Pilot Partners Across Industries
                                    </p>
                                    <p className="text-primary-800">
                                        {CONTACT_DETAILS.company.name} is looking for forward-thinking organizations in
                                        healthcare, energy, forestry, and manufacturing for collaborative pilot implementations.
                                        Pilot partners receive full
                                        four-element implementation on one critical use case with direct developer
                                        access and flexible arrangements.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Platform Preview Section */}
                    <section className="mb-16">
                        <div className="bg-white rounded-2xl p-8 shadow-lg">
                            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
                                Four Elements, One Unified Platform
                            </h2>
                            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
                                From time-series forecasting to instant knowledge retrieval - see how the four-element
                                approach delivers actionable intelligence tailored to your industry.
                            </p>

                            <div className="max-w-5xl mx-auto">
                                <img
                                    src='/images/4-quadrant-alt.webp'
                                    alt={`${CONTACT_DETAILS.company.name} Platform showing Time Series Forecasts, Risk Indicators, Optimization Suggestions, and Knowledge Base Search`}
                                    className="w-full h-auto rounded-lg shadow-md"
                                    loading="lazy"
                                />
                            </div>

                            {/* Optional: Four-element callouts below image */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-4xl mx-auto">
                                <div className="text-center">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                                        <span className="text-blue-600 font-bold">1</span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Monitor</p>
                                    <p className="text-xs text-gray-500">Real-Time Data Collection</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                                        <span className="text-green-600 font-bold">2</span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Predict</p>
                                    <p className="text-xs text-gray-500">Risk Indicators</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                                        <span className="text-purple-600 font-bold">3</span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Optimize</p>
                                    <p className="text-xs text-gray-500">Actionable Suggestions</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-2">
                                        <span className="text-orange-600 font-bold">4</span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-700">Preserve</p>
                                    <p className="text-xs text-gray-500">Knowledge Search</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Services Grid */}
                    <div className="space-y-16 mb-16">
                        {services.map((service, index) => (
                            <section key={service.id} id={service.id} className="scroll-mt-20">
                                <Card
                                    className={`p-8 hover:shadow-xl transition-shadow cursor-pointer ${
                                        index % 2 === 1 ? 'bg-gradient-to-r from-gray-50 to-blue-50' : 'bg-white'
                                    }`}
                                    onClick={() => handleServiceClick(service.title)}
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                                        {/* Service Info */}
                                        <div className="lg:col-span-2">
                                            <div className="flex items-center mb-4">
                                                {service.icon}
                                                <div className="ml-4">
                                                    <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                                                    <p className="text-lg text-gray-600">{service.subtitle}</p>
                                                </div>
                                            </div>

                                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                                {service.description}
                                            </p>

                                            <div className="mb-6">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits:</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {service.benefits.map((benefit, idx) => (
                                                        <div key={idx} className="flex items-start">
                                                            <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                                            <span className="text-gray-700">{benefit}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Ideal For Box */}
                                        <div className="lg:col-span-1">
                                            <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Ideal For:</h3>
                                                <p className="text-gray-700 mb-4">{service.ideal}</p>

                                                <div className="space-y-2">
                                                    <Link
                                                        to="/assessment"
                                                        className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-lg transition-colors"
                                                        onClick={() => handleCTAClick(`${service.title}-assessment`)}
                                                    >
                                                        Get Assessment
                                                    </Link>
                                                    <Link
                                                        to={`/contact?source=services-${service.id}`}
                                                        className="block w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white text-center py-2 px-4 rounded-lg transition-colors"
                                                        onClick={() => handleCTAClick(`${service.title}-contact`)}
                                                    >
                                                        Learn More
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </section>
                        ))}
                    </div>

                    {/* Process Section */}
                    <section className="mb-16 bg-white rounded-2xl p-8 shadow-lg">
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
                            The {CONTACT_DETAILS.company.name} Process
                        </h2>
                        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                            A proven four-step approach to implementing predictive intelligence in your operations
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {[
                                { step: '1', title: 'Assess', description: 'Evaluate your operations, data sources, and strategic priorities' },
                                { step: '2', title: 'Design', description: 'Create a customized four-element implementation plan' },
                                { step: '3', title: 'Implement', description: 'Integrate systems and deploy the platform with minimal disruption' },
                                { step: '4', title: 'Train & Support', description: 'Enable your team and provide ongoing optimization' }
                            ].map((phase, index) => (
                                <div key={index} className="text-center">
                                    <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                                        {phase.step}
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{phase.title}</h3>
                                    <p className="text-gray-600">{phase.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* FAQ Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {[
                                {
                                    q: "What exactly is included in the pilot program?",
                                    a: "The pilot implements all four elements (Monitor, Predict, Optimize, Preserve) on one critical use case over 3-6 months. You receive complete system setup, team training, ongoing support, and direct access to the development team. There's no long-term commitment required."
                                },
                                {
                                    q: "How much does a pilot program cost?",
                                    a: `${CONTACT_DETAILS.company.name} negotiates pilot pricing on a case-by-case basis. Pilot partners receive flexible arrangements in exchange for helping validate the cross-industry approach. Contact us to discuss a structure that works for your organization and budget.`
                                },
                                {
                                    q: "How quickly can implementation begin?",
                                    a: "Most pilots begin within 2-4 weeks after the initial assessment. The exact timeline depends on your system complexity and data availability, but we prioritize minimal disruption to ongoing operations."
                                },
                                {
                                    q: "Do we need dedicated IT or technical staff?",
                                    a: `No. ${CONTACT_DETAILS.company.name} provides comprehensive implementation support and handles all technical complexity. Your team focuses on operations while we manage system integration, configuration, and ongoing technical maintenance.`
                                },
                                {
                                    q: "What happens after the pilot period ends?",
                                    a: `After 3-6 months, you'll have proven ROI metrics specific to your industry. Successful pilots can scale to additional operations. If results don't meet expectations, there's no obligation to continue - that's the ${CONTACT_DETAILS.company.name} approach.`
                                },
                                {
                                    q: "Can we start with just one or two elements instead of all four?",
                                    a: "While pilots include all four elements for complete value, we can emphasize different elements based on your biggest pain points. Some organizations need Predict most urgently, others prioritize Preserve. The implementation is customized to your priorities."
                                }
                            ].map((faq, index) => (
                                <Card key={index} className="p-6 bg-white hover:shadow-lg transition-shadow">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                                    <p className="text-gray-700">{faq.a}</p>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="text-center bg-gradient-to-r from-primary-600 to-blue-700 text-white rounded-2xl p-12">
                        <h2 className="text-3xl font-bold mb-6">
                            Ready to Transform Your Operations?
                        </h2>
                        <p className="text-xl mb-4 text-blue-100">
                            Start with a free assessment to discover how the four-element approach applies to your
                            specific industry and operational challenges.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/assessment"
                                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-primary-900 rounded-lg hover:bg-gray-100 transition-colors"
                                onClick={() => handleCTAClick('final-assessment')}
                            >
                                Take Free Assessment
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                            <Link
                                to="/contact?source=services-pilot"
                                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-900 transition-colors"
                                onClick={() => handleCTAClick('final-contact')}
                            >
                                Discuss Pilot Program
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};